// Matches the legacy `Bt` atom in app/frontend/index.html.
// Variants: default | primary | danger | ghost

import { clsx } from "clsx";
import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "default" | "primary" | "danger" | "ghost";

interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  children: ReactNode;
  variant?: Variant;
  full?: boolean;
  small?: boolean;
}

const variantClasses: Record<Variant, string> = {
  default: "bg-bg3 border-stroke text-content",
  primary: "bg-accent-dark border-accent text-white",
  danger: "bg-danger/20 border-danger text-danger",
  ghost: "bg-transparent border-transparent text-muted",
};

export function Button({
  children,
  variant = "default",
  full,
  small,
  disabled,
  className,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled}
      className={clsx(
        "border font-mono font-semibold tracking-[0.02em] transition-all duration-150 rounded",
        // Default buttons hit the 44px Apple HIG touch target on mobile
        // and dial back to ~36px on md+ so dense desktop pages don't feel
        // chunky. `small` variant stays chip-sized for filter/tab bars
        // where density matters more than touch reach.
        small
          ? "px-[10px] py-[4px] text-[11px]"
          : "px-4 py-2 min-h-[44px] md:min-h-[36px] text-[13px]",
        full ? "w-full" : "w-auto",
        disabled ? "opacity-40 cursor-default" : "cursor-pointer",
        variantClasses[variant],
        className,
      )}
    >
      {children}
    </button>
  );
}
